if (!customElements.get('m-slideshow')) {
  class OptimizedSlideshow extends HTMLElement {
    constructor() {
      super();
      // Pre-bind methods
      this.handleChange = this.handleChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this._cacheElements();
      this._setupOptions();
      this._initSlider();
    }

    _cacheElements() {
      // Cache DOM elements once
      this.elements = {
        sliderContainer: this.querySelector('.swiper-container'),
        nextButton: this.querySelector('.m-slider-controls__button-next'),
        prevButton: this.querySelector('.m-slider-controls__button-prev'),
        pagination: this.querySelector('.swiper-pagination'),
        videos: Array.from(this.querySelectorAll('video'))
      };
    }

    _setupOptions() {
      // Convert data attributes once
      this.options = {
        autoplay: this.dataset.autoplay === 'true',
        autoplaySpeed: parseInt(this.dataset.autoplaySpeed) * 1000,
        showArrows: this.dataset.enableArrows === 'true',
        showDots: this.dataset.enableDots === 'true',
        adaptHeight: this.dataset.slideHeight === 'adapt',
        designMode: Shopify.designMode
      };
    }

    _initSlider() {
      if (!window.MinimogLibs?.Swiper) return;

      // Configure Swiper with performance optimizations
      this.swiper = new MinimogLibs.Swiper(this.elements.sliderContainer, {
        init: false,
        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: true,
        effect: 'slide', // Cheaper than fade
        speed: 300, // Reduced from 1000ms
        preloadImages: false,
        lazy: true,
        watchSlidesProgress: true,
        resistanceRatio: 0,
        autoplay: this.options.autoplay ? {
          delay: this.options.autoplaySpeed,
          disableOnInteraction: true,
          waitForTransition: true
        } : false,
        navigation: this.options.showArrows ? {
          nextEl: this.elements.nextButton,
          prevEl: this.elements.prevButton
        } : false,
        pagination: this.options.showDots ? {
          el: this.elements.pagination,
          clickable: true,
          bulletClass: 'm-dot',
          bulletActiveClass: 'm-dot--active'
        } : false,
        on: {
          init: () => {
            this._handleFirstInit();
          },
          slideChange: this.handleChange
        }
      });

      this.swiper.init();

      if (this.options.designMode) {
        this._setupDesignMode();
      }
    }

    _handleFirstInit() {
      // Use RAF instead of setTimeout
      requestAnimationFrame(() => {
        this.swiper.updateSize();
        this.swiper.updateSlides();
      });
    }

    handleChange(swiper) {
      const { activeIndex, slides } = swiper;
      const currentSlide = slides[activeIndex];
      
      // Handle video playback
      this._manageVideo(currentSlide);
    }

    _manageVideo(slide) {
      const video = slide?.querySelector('video');
      if (!video) {
        this.elements.videos.forEach(v => v.pause());
        return;
      }

      // Use promise chain for better control
      video.play()
        .then(() => {
          this.elements.videos.forEach(v => v !== video && v.pause());
        })
        .catch(() => { /* Handle autoplay restrictions */ });
    }

    _setupDesignMode() {
      // Use passive event listener
      document.addEventListener('shopify:block:select', this.onBlockSelect, {
        passive: true
      });
    }

    onBlockSelect(ev) {
      const index = Number(ev.target.dataset.slide);
      if (this.swiper) {
        this.swiper.slideToLoop(index);
      }
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy();
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }
    }
  }

  customElements.define('m-slideshow', OptimizedSlideshow);
}